/* eslint max-classes-per-file: 0 */

import clsx from 'clsx';
import Storefront from '../utils/storefront';
import { formatMoney, removeEdgesAndNodes } from '../utils/storefront/format-resource';
import Slider from '../slider';
import { throttle } from '../utils/global-utilities';
import { productGroupGet } from '../utils/storefront/queries';

// Helper function to encode the product ID
function encodeProductId(productId) {
  return btoa(`gid://shopify/Product/${productId}`);
}

// Fetch product group data using Storefront class
async function fetchProductGroupData(productId) {
  const storefront = Storefront.getInstance();
  const encodedProductId = encodeProductId(productId); // Encode the product ID
  const response = await storefront.request({
    query: productGroupGet,
    variables: { id: encodedProductId }
  });

  // Use removeEdgesAndNodes to format the data
  const formattedData = removeEdgesAndNodes(
    response.data?.product
  );
  return formattedData;
}

class ProductCard extends HTMLElement {
  connectedCallback() {
    this.slider = null;
    this.slidersContainer = this.querySelectorAll('[data-product-card]');
    this.badges = this.querySelectorAll('[data-badge]');
    this.mobileQuickViewButton = this.querySelector('[data-mobile-quick-view]');
    this.mobileQuickViewVariantId = this.querySelector('[data-product-card]').getAttribute('data-variant-id');
    this.initSlider();
    this.equalizeProductCardContentHeight();
    this.enableQuickView();

    this.throttledEqualizeHeight = throttle(
      this.equalizeProductCardContentHeight, 250
    );

    this.startInjectingData();

    window.addEventListener('resize', this.throttledEqualizeHeight);
    window.addEventListener('bpChange', this.throttledEqualizeHeight);
    document.addEventListener('equalizeProductCards', this.equalizeProductCardContentHeight);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.throttledEqualizeHeight);
    window.removeEventListener('bpChange', this.throttledEqualizeHeight);
    document.removeEventListener('equalizeProductCards', this.equalizeProductCardContentHeight);
  }

  handleBpChange = () => {
    if (window.innerWidth > 1023) {
      return false;
    }
    return true;
  };

  resetSwatches = () => {
    this.swatches = this.querySelectorAll('[data-product-card-swatches] [data-swatch]');
    this.swatches.forEach((swatch) => {
      swatch.classList.remove('border-black');
      swatch.setAttribute('aria-current', 'false');
    });
  }

  hideQuickView = (qButton) => {
    let parentWithAttribute = qButton;
    while (parentWithAttribute && !parentWithAttribute.hasAttribute('data-template-name')) {
      parentWithAttribute = parentWithAttribute.parentElement;
    }
    if (parentWithAttribute && parentWithAttribute.getAttribute('data-template-name') === 'product') {
      qButton.classList.add('!hidden');
      const buttonElement = qButton.querySelector('button');
      buttonElement.classList.add('!hidden');
    }
  }



  initSlider = () => {
    if (this.querySelector('[data-product-card-carousel]')) {
      if (this.slider) {
        this.slider.destroy();
      }
      this.slider = new Slider({
        el: this.querySelector('[data-product-card-carousel]'),
        options: {
          slidesPerView: 1,
          pagination: {
            el: this.querySelector('[data-product-card-pagination]'),
            clickable: false
          },
          navigation: {
            nextEl: this.querySelector('[data-product-card-next]'),
            prevEl: this.querySelector('[data-product-card-prev]'),
          }
        }
      });
    }
    return this.slider
  };

  enableQuickView = () => {
    this.slidersContainer = this.querySelector('[data-product-card]');
    this.mobileQuickViewButton = this.querySelector('[data-mobile-quick-view]');
    this.quickViewButtons = this.slidersContainer.querySelectorAll('[data-quick-view]');
    this.quickViewMoreButton = this.querySelector('[data-more-swatches] [data-product-url]');
    const quickViewVariantId = this.slidersContainer.getAttribute('data-variant-id');
    const productHandle = this.slidersContainer.getAttribute('data-product-handle');
    const productId = this.slidersContainer.getAttribute('data-product-id');
    this.hideQuickView(this.quickViewButton);
    this.quickViewMoreButton.addEventListener('click', (e) => {
      e.preventDefault();
      const quickViewOpened = new CustomEvent('quick-view-opened', {
        detail: {
          productHandle: this.slidersContainer.getAttribute('data-product-handle'),
          productId: this.slidersContainer.getAttribute('data-product-id'),
          currentVariant: this.slidersContainer.getAttribute('data-variant-id'),
          clickedButton: e.target,
        }
      });
      document.dispatchEvent(quickViewOpened);
    }); 
    this.quickViewButtons.forEach((qViewButton) => {
      qViewButton.addEventListener('click', (e) => {
        e.preventDefault();
        const quickViewOpened = new CustomEvent('quick-view-opened', {
          detail: {
            productHandle,
            productId,
            currentVariant: quickViewVariantId,
            clickedButton: e.target,
          }
        });
        document.dispatchEvent(quickViewOpened);
      });
    });

    this.mobileQuickViewButton.replaceWith(
      this.mobileQuickViewButton.cloneNode(true)
    );
    this.mobileQuickViewButton = this.querySelector('[data-mobile-quick-view]');

    this.mobileQuickViewButton?.addEventListener('click', (e) => {
      e.preventDefault();
      const mobileQuickViewOpened = new CustomEvent('quick-view-opened', {
        detail: {
          productHandle,
          productId,
          currentVariant: quickViewVariantId,
          clickedButton: e.target,
        }
      });
      document.dispatchEvent(mobileQuickViewOpened);
    });
    this.hideQuickView(this.mobileQuickViewButton);
  }

  enableSwatches = () => {
    this.swatches = this.querySelectorAll('[data-product-card-swatches] [data-swatch]');
    this.swatches?.forEach((swatch) => {
      swatch.addEventListener('click', () => {
        const swatchVariantId = swatch.getAttribute('data-variant-id').split('/').pop();
        this.resetSwatches();
        swatch.classList.add('border', 'border-black');
        swatch.setAttribute('aria-current', 'true');
        // Find the variant data from the existing product group data
        const variantData = this.findVariantData(swatchVariantId);
        this.updateProductCard(
          variantData.variant,
          variantData.productId,
          variantData.productHandle,
          variantData.productTitle,
          variantData.productCategory,
          variantData.productBadge
        ); // Update the product card with new variant data
      });
    });
  }

  findVariantData = (variantId) => {
    const { productGroupData, product: prod } = this;
    for (const product of [prod]
      .concat(productGroupData.products.references)) {
      for (const variant of product.variants) {
        if (variant.id.split('/').pop() === variantId) {
          return {
            variant,
            productId: product.id,
            productHandle: product.handle,
            productTitle: product.title,
            productCategory: variant.swatch.reference.label.value,
            productBadge: variant.badge?.value
          };
        }
      }
    }
    return null;
  }

  isVisible = (element) => {
    if (element.classList.contains('hidden')) {
      return false;
    }
    return true;
  }

  equalizeProductCardContentHeight = () => {
    const isMobile = this.handleBpChange();
    const itemsPerRow = isMobile ? 2 : 4;
    const productCards = Array.from(document.querySelectorAll('[data-product-card]')).filter(
      card => this.isVisible(card)
    );

    let maxDescriptionHeight = 0;
    let maxTitleHeight = 0;
    let rowHasDiscount = false;
    let cardDescriptionHeights = [];
    let cardTitleHeights = [];
    let cardPriceHeights = [];

    productCards.forEach((card, index) => {
      const productCardContent = card.querySelector('[data-product-card-content]');
      const productCardDescription = productCardContent?.querySelector('.product-card-description');
      const productCardTitle = productCardContent?.querySelector('.product-card-name');
      const mobileDiscount = productCardContent?.querySelector('[data-price-mobile-discount]');
      const productCardPrice = productCardContent?.querySelector('[data-price]');

      if (productCardContent) {
        productCardContent.style.height = '';
      }
      if (productCardDescription) {
        productCardDescription.style.height = '';
      }
      if (productCardTitle) {
        productCardTitle.style.height = '';
      }
      if (productCardPrice) {
        productCardPrice.style.height = '';
      }

      if (productCardDescription) {
        const descriptionHeight = productCardDescription.offsetHeight;
        maxDescriptionHeight =
          Math.max(maxDescriptionHeight, descriptionHeight);
        cardDescriptionHeights.push({ index, descriptionHeight });
      }
      if (productCardTitle) {
        const titleHeight = productCardTitle.offsetHeight;
        maxTitleHeight = Math.max(maxTitleHeight, titleHeight);
        cardTitleHeights.push({ index, titleHeight });
      }
      if (productCardPrice) {
        const priceHeight = productCardPrice.offsetHeight;
        cardPriceHeights.push(
          { cardIndex: index, priceHeight, hasMobileDiscount: !!mobileDiscount }
        );
        if (isMobile && mobileDiscount) {
          rowHasDiscount = true;
        }
      }

      if (
        (index + 1) % itemsPerRow === 0 ||
        index === productCards.length - 1
      ) {
        cardDescriptionHeights.forEach(cardHeight => {
          const cardDescription = productCards[cardHeight.index].querySelector('.product-card-description');
          if (cardDescription) {
            cardDescription.style.height = `${maxDescriptionHeight}px`;
          }
        });
        cardTitleHeights.forEach(cardHeight => {
          const cardTitle = productCards[cardHeight.index].querySelector('.product-card-name');
          if (cardTitle) {
            cardTitle.style.height = `${maxTitleHeight}px`;
          }
        });
        cardPriceHeights.forEach((
          { cardIndex, priceHeight, hasMobileDiscount }
        ) => {
          const cardPrice = productCards[cardIndex].querySelector('[data-price]');
          if (cardPrice) {
            if (isMobile && rowHasDiscount && !hasMobileDiscount) {
              const maxDiscountHeight = Math.max(...cardPriceHeights.filter(
                ch => ch.hasMobileDiscount).map(
                  ch => ch.priceHeight
                )
              );
              cardPrice.style.height =
                `${priceHeight + maxDiscountHeight - 5}px`;
            }
          }
        });

        maxDescriptionHeight = 0;
        maxTitleHeight = 0;
        rowHasDiscount = false;
        cardDescriptionHeights = [];
        cardTitleHeights = [];
        cardPriceHeights = [];
      }
    });
  };

  async fetchAndInjectProductGroupData() {
    const divWithProductId = this.querySelector('div');
    const productId = divWithProductId.getAttribute('data-product-id');
    const currentVariantId = divWithProductId.getAttribute('data-variant-id'); // Get current variant ID
    const product = await fetchProductGroupData(productId);
    const productGroupData = product.productGroup.reference;
    this.productGroupData = productGroupData; // Store productGroupData in the instance
    this.product = product;
    if (productGroupData) {
      this.injectSwatches(productGroupData, currentVariantId);
    }
  }

  injectSwatches(productGroupData, currentVariantId) {
    const swatchesContainer = this.querySelector('[data-product-card-swatches]');
    const swatches = productGroupData.products.references.filter((product) =>
      !product.variants.some(variant => variant.id.split('/').pop() === currentVariantId)
    );
    swatches.slice(0, 4).forEach((product, i) => {
      product.variants.forEach(variant => {
        const swatchHtml = this.createSwatchHtml(
          variant, i
        );
        swatchesContainer.children[swatchesContainer.children.length - 1]
          .insertAdjacentHTML(
            'beforebegin',
            swatchHtml
          )
      });
    });

    this.toggleMoreSwatches(swatches.length + 1)
    this.enableSwatches();
  }

  toggleMoreSwatches(numTotalSwatches) {
    const classes = clsx(
      {
        'block md:hidden': numTotalSwatches > 3 && numTotalSwatches <= 5,
        'hidden': numTotalSwatches <= 3,
        'block': numTotalSwatches > 5,
      }
    );
    const el = this.querySelector('[data-more-swatches]');
    el.classList.remove('hidden');
    if (classes) {
      el.classList.add(...classes.split(' '));
    }
  }

  createSwatchHtml(variant, index) {
    const swatchImage = variant?.swatch?.reference?.
      image?.reference?.image?.src;
    const swatchLabel = variant?.swatch?.reference?.label?.value;
    const baseClasses = clsx(
      "w-12 h-[24px] cursor-pointer leading-none border border-transparent flex items-center justify-center text-12 bg-grey-7",
      index >= 2 && "hidden md:block"
    );
    /* eslint-disable */
    return `
      <button class="${baseClasses}" data-swatch data-variant-id="${variant.id}" aria-current="false">
        <img src="${swatchImage}" alt="${swatchLabel}" class="image w-full h-full swatch object-cover">
      </button>
    `;
    /* eslint-enable */
  }

  updateProductCard = (
    variant,
    productId,
    productHandle,
    productTitle,
    productCategory,
    productBadge
  ) => {
    const dynamicProductCard = this.closest('dynamic-product-card');
    const productCardDiv = this.querySelector(`[data-product-card]`);
    const carouselEnabled = productCardDiv.getAttribute('data-carousel-enabled');
    const variantUrl = `/products/${productHandle}?variant=${variant.id.split('/').pop()}`;

    if (productCardDiv) {
      const imagesHtml = this.generateImagesHtml(
        variant, carouselEnabled, variantUrl
      );
      const compareAtPriceHtml = variant.compareAtPrice ? `
        <span class="line-through text-gray-500">
        ${formatMoney({
        amount: variant.compareAtPrice.amount,
        currencyCode: window.TMW?.localization?.currencyCode || 'USD',
      })}
        </span>
      ` : '';

      // Update the swiper-wrapper content
      const swiperWrapper = productCardDiv.querySelector('.swiper-wrapper');
      if (swiperWrapper) {
        swiperWrapper.innerHTML = imagesHtml;
      }

      const numericProductId = productId.split('/').pop();
      const numericVariantId = variant.id.split('/').pop();
      // Update data-variant-id
      productCardDiv.setAttribute('data-variant-id', numericVariantId);
      productCardDiv.setAttribute('data-product-id', numericProductId);
      productCardDiv.setAttribute('data-product-handle', productHandle);
      dynamicProductCard?.setAttribute('handle', productHandle);
      // Update data-product-card-title
      const productCardTitle = productCardDiv.querySelector('[data-product-card-title]');
      if (productCardTitle) {
        productCardTitle.href = variant.url;
        productCardTitle.innerHTML = productTitle;
      }

      // Update data-product-card-description
      const productCardDescription = productCardDiv.querySelector('[data-product-card-description]');
      if (productCardDescription) {
        productCardDescription.innerHTML = productCategory;
      }

      // Update product badge
      const productCardBadge = productCardDiv.querySelector('[data-badge]');
      if (productCardBadge) {
        productCardBadge.innerHTML = ''
      }

      if (productCardBadge && productBadge) {
        productCardBadge.innerHTML = productBadge
        productCardBadge.classList.remove('hidden');
      } else {
        productCardBadge?.classList.add('hidden');
      }

      // Update data-price
      const productCardPrice = productCardDiv.querySelector('[data-price]');
      if (productCardPrice) {
        productCardPrice.innerHTML = `
          ${compareAtPriceHtml}
          <span>
            ${formatMoney({
          amount: variant.price.amount,
          currencyCode: window.TMW?.localization?.currencyCode || 'USD',
        })}
          </span>
        `;
      }

      if (carouselEnabled) this.initSlider(); // Initialize the swiper for the new content
      this.enableQuickView();
      this.equalizeProductCardContentHeight();
    }
  }

generateImagesHtml = (variant, carouselEnabled, variantUrl) => {
  let imagesHtml;
  if (carouselEnabled) {
    /* eslint-disable */
    imagesHtml = variant.images.references
      .filter(image => image.image)
      .map((image, index) => `
        <div class='swiper-slide' role='group' aria-roledescription='slide' aria-label='Slide ${index + 1} of ${variant.images.references.filter(img => img.image).length}'>
          <a class='relative block no-underline w-full' href='${variantUrl}'>
            <img 
            src='${image.image.desktopSrc}' 
            alt='${variant.title}' 
            class='w-full h-full object-cover' 
            width='600'
            height='600'/>
            <div class='hidden opacity-0 transition-opacity duration-300 lg:block lg:absolute lg:w-full lg:bottom-2.5 lg:left-0 lg:px-1.5 group-hover:opacity-100' data-quick-view>
              <button class='utility-4 text-primary bg-white bg-opacity-80 py-3.5 px-1.5 w-full hover:border hover:border-black'>
                Quick View
              </button>
            </div>
          </a>
        </div>
      `).join('');
    /* eslint-enable */
  } else {
    /* eslint-disable */
    imagesHtml = `
      <div class='swiper-slide' role='group' aria-roledescription='slide' aria-label='Slide 1 of 1'>
        <a class='relative block no-underline w-full' href='${variantUrl}'>
          <img 
          src='${variant.images.references[0]?.image.desktopSrc}' 
          alt='${variant.title}' 
          class='w-full h-full object-cover' 
          width='600'
          height='600'
          />
          <div class='hidden opacity-0 transition-opacity duration-300 lg:block lg:absolute lg:w-full lg:bottom-2.5 lg:left-0 lg:px-1.5 group-hover:opacity-100' data-quick-view>
            <button class='utility-4 text-primary bg-white bg-opacity-80 py-3.5 px-1.5 w-full hover:border hover:border-black'>
              Quick View
            </button>
          </div>
        </a>
      </div>
    `;
    /* eslint-enable */
  }
  return imagesHtml;
}

  startInjectingData = () => {
    const observerOptions = {
      root: null,
      rootMargin: '100px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.fetchAndInjectProductGroupData();
          obs.unobserve(entry.target); // Stop observing after loading
        }
      });
    }, observerOptions);

    observer.observe(this);
  }
}

class DynamicProductCard extends HTMLElement {
  constructor() {
    super();

    this.variantId = this.getAttribute('variant-id');
    this.handle = this.getAttribute('handle');
    this.template = this.getAttribute('template') || 'product-card';
    this.urlPostfix = this.getAttribute('url-postfix') || '';
    this.displaySwatches = this.getAttribute('display-swatches') === 'true';
    this.CACHE = {};
    this.VARIANT_HANDLE_CACHE = {};
  }

  connectedCallback() {
    this.loadProduct();
  }

  async loadProduct() {
    const { handle } = this;

    if (!handle) return;

    const cacheKey = `${handle}-${this.variantId}-${this.template}`;
    if (this.CACHE[cacheKey]) {
      this.renderProduct(this.CACHE[cacheKey]);
      return;
    }

    try {
      const response = await fetch(`/products/${handle}?view=${this.template}&layout=none${this.variantId ? `&variant=${this.variantId}` : ''}`);
      let text = await response.text();

      if (text.includes('<html') || text.includes('<body')) {
        throw new Error('Invalid response');
      }
   
      if (this.displaySwatches) {
        text = text.replace(
          // eslint-disable-next-line max-len
          /(<div[^>]*class=['"][^'"]*\bswatches\b[^'"]*)\bflex\b([^'"]*['"][^>]*data-product-card-swatches[^>]*>)/gi,
          '$1hidden$2'
        );
      }
      this.CACHE[cacheKey] = text;
      this.renderProduct(text);
    } catch (error) {
      console.error('Error loading product:', error);
    }
  }

  renderProduct(content) {
    this.innerHTML = `<div>${content}</div>`;

    this.updatePostfix();
  }

  updatePostfix() {
    if (!this.urlPostfix || !window.__VENDORS__?.nosto?.enabled) return;
    const links = this.querySelectorAll('[data-product-url]');
    links.forEach((link) => {
      const href = new URL(link.href || link.getAttribute('product-url'));
      const [key, value] = this.urlPostfix.split('=');
      if (key && value) {
        href.searchParams.append(key, value);
        // eslint-disable-next-line no-unused-expressions, no-param-reassign
        link.href ? (link.href = href.toString()) :
        link.setAttribute('product-url', href.toString());
      }
    });
  }
}

export default () => {
  if (!customElements.get('product-card'))
    customElements.define('product-card', ProductCard);

  if (!customElements.get('dynamic-product-card'))
    customElements.define('dynamic-product-card', DynamicProductCard);
}
